import { createContext, useContext, useRef } from 'react';
import { createStore, useStore } from 'zustand';
import { SelectedPreviewState } from '../components/ProductPreviews/types';

interface SelectedPreviewProps {
  selectedPreview: SelectedPreviewState;
}

interface SelectedPreviewStoreState extends SelectedPreviewProps {
  setSelectedPreview: (newPreview: SelectedPreviewState) => void;
}

type SelectedPreviewStore = ReturnType<typeof createSelectedPreviewStore>;

const createSelectedPreviewStore = (
  initProps?: Partial<SelectedPreviewProps>
) => {
  const DEFAULT_PROPS: SelectedPreviewProps = {
    selectedPreview: undefined,
  };
  return createStore<SelectedPreviewStoreState>()((set) => ({
    ...DEFAULT_PROPS,
    ...initProps,
    setSelectedPreview: (newPreview) =>
      set(() => ({ selectedPreview: newPreview })),
  }));
};

const SelectedPreviewContext = createContext<SelectedPreviewStore | null>(null);

export const CurrentPreviewProvider = ({
  children,
  initialPreview,
}: {
  children: React.ReactNode;
  initialPreview: SelectedPreviewState;
}) => {
  const store = useRef(
    createSelectedPreviewStore({ selectedPreview: initialPreview })
  ).current;
  return (
    <SelectedPreviewContext.Provider value={store}>
      {children}
    </SelectedPreviewContext.Provider>
  );
};

export const useCurrentPreview = () => {
  const store = useContext(SelectedPreviewContext);
  if (!store)
    throw new Error(
      'useCurrentPreview hook must be used within a SelectedPreviewContext.Provider'
    );
  const selectedPreview = useStore(store, (s) => s.selectedPreview);
  const setSelectedPreview = useStore(store, (s) => s.setSelectedPreview);

  return { selectedPreview, setSelectedPreview };
};
